import React, { Fragment, useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { NotificationData } from './shared'
import {
  MustacheWithClickHandler,
  MustacheClickHandler
} from '../../legacy-design-system/mustache/mustache-with-click-handler'
import { MarkNotificationReadProps } from './api/use-notifications-data'
import { Text } from '@contrast-security-inc/design-system-typography'
import { NotificationIcon } from './notification-icon'
import {
  NotificationRowLayout,
  NotificationRowLayoutGetRef
} from './notification-row-layout'

export type NotificationRowData = Pick<
  NotificationData,
  | 'id'
  | 'sourceType'
  | 'type'
  | 'read'
  | 'timestamp'
  | 'mustacheTemplate'
  | 'properties'
  | 'messageVariables'
>

export function NotificationRow({
  index,
  style,
  notification: {
    id,
    sourceType,
    type,
    read,
    timestamp,
    mustacheTemplate,
    properties,
    messageVariables
  },
  mustacheTemplateClickHandler,
  isMarkAsReadLoading,
  markNotificationRead,
  getRef
}: {
  index: number
  style: React.CSSProperties
  notification: NotificationRowData
  mustacheTemplateClickHandler: MustacheClickHandler
  isMarkAsReadLoading: boolean
  markNotificationRead: (args: MarkNotificationReadProps) => void
  getRef: NotificationRowLayoutGetRef
}) {
  const markAsRead = useCallback(() => {
    if (!read && !isMarkAsReadLoading) {
      markNotificationRead({ notificationId: id })
    }
  }, [read, isMarkAsReadLoading, markNotificationRead, id])
  const MemoizedContent = useMemo(
    () => (
      <MustacheWithClickHandler
        testId="notification-content"
        tagName="span"
        views={messageVariables}
        template={mustacheTemplate}
        clickHandler={(...args: any[]) => {
          mustacheTemplateClickHandler(...args)
          markAsRead()
        }}
        clickMethodTarget={
          type === 'APP_ONBOARDING_FAILED' ? type : 'DOWNLOAD_ASYNC_REPORT'
        }
      />
    ),
    [
      mustacheTemplate,
      mustacheTemplateClickHandler,
      markAsRead,
      messageVariables,
      type
    ]
  )

  return (
    <NotificationRowLayout
      className={`notification-row`}
      testId={`notification-row-${id}`}
      index={index}
      style={style}
      read={read}
      timestamp={timestamp}
      getRef={getRef}
      onClick={markAsRead}
      Icon={
        <NotificationIcon
          type={type}
          sourceType={sourceType}
          properties={properties}
        />
      }
      Content={MemoizedContent}
      Metadata={
        <NotificationProperties
          sourceType={sourceType}
          properties={properties}
        />
      }
    />
  )
}

function NotificationProperties({
  sourceType,
  properties
}: {
  sourceType: NotificationRowData['sourceType']
  properties: NotificationRowData['properties']
}) {
  const { formatMessage } = useIntl()
  const show = sourceType !== 'APPROVAL_WORKFLOW' && properties.length > 0
  return show ? (
    <>
      <span style={{ margin: '0 8px 0 8px' }}>
        <Text font="metadata" color="neutral70">
          |
        </Text>
      </span>
      {properties.map(({ name, value }, index) => (
        <Fragment key={index}>
          <Text font="metadataStrong" color="neutral70">
            {`${name}: `}
          </Text>
          <Text font="metadata" color="neutral70">
            {formatMessage({ id: value, defaultMessage: value })}
          </Text>
        </Fragment>
      ))}
    </>
  ) : (
    <div />
  )
}
